export type AdmissionRequirementMode = 'hidden' | 'optional' | 'required';
export type AdmissionRequirementScope = 'per_student' | 'family';
export type AdmissionDocumentIcon = 'file' | 'photo';

export type AdmissionDocumentRequirementRow = {
  settingId: string;
  slotId: string;
  label: string;
  description: string;
  accept: string;
  icon: AdmissionDocumentIcon;
  mode: AdmissionRequirementMode;
  visible: boolean;
  required: boolean;
  gradeScope: string[];
  scope: AdmissionRequirementScope;
};

export type AdmissionAgreementRow = {
  id: string;
  label: string;
  text: string;
  mode: AdmissionRequirementMode;
  visible: boolean;
  required: boolean;
};

export type AdmissionVaccinationPolicyLike = {
  policyText?: string | null;
  allowReligiousExemptions?: boolean | null;
  requireBothParentsAcknowledgment?: boolean | null;
};

export type AdmissionsRequirementStudent = {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  grade?: string | null;
};

export type AdmissionsRequiredUpload = {
  key: string;
  label: string;
  fileName: string;
};

type AdmissionDocumentDefinition = {
  settingId: string;
  slotId: string;
  label: string;
  description: string;
  accept: string;
  icon: AdmissionDocumentIcon;
  defaultMode: AdmissionRequirementMode;
  defaultGradeScope?: string[];
  scope: AdmissionRequirementScope;
  legacyIds?: string[];
};

type AdmissionAgreementDefinition = {
  id: string;
  label: string;
  text: string;
  defaultMode: AdmissionRequirementMode;
};

const REPORT_CARD_GRADE_SCOPE = [
  'Grade 1',
  'Grade 2',
  'Grade 3',
  'Grade 4',
  'Grade 5',
  'Grade 6',
  'Grade 7',
  'Grade 8',
] as const;

export const ADMISSIONS_REPORT_CARD_GRADE_SCOPE_OPTIONS = [...REPORT_CARD_GRADE_SCOPE] as const;

export const ADMISSIONS_GRADE_SCOPE_OPTIONS = [
  'Nursery',
  'Kindergarten',
  'Pre-1A',
  ...REPORT_CARD_GRADE_SCOPE,
] as const;

const DOCUMENT_DEFINITIONS: AdmissionDocumentDefinition[] = [
  {
    settingId: 'transcripts',
    slotId: 'previous-report-card',
    label: 'Previous Report Card',
    description: 'Required for Grade 1 through Grade 8.',
    accept: '.pdf,.jpg,.jpeg,.png',
    icon: 'file',
    defaultMode: 'hidden',
    defaultGradeScope: [...REPORT_CARD_GRADE_SCOPE],
    scope: 'per_student',
    legacyIds: ['previous_report_card', 'previous-report-card'],
  },
  {
    settingId: 'birth_certificate',
    slotId: 'birth-certificate',
    label: 'Birth Certificate',
    description: 'Copy of original.',
    accept: '.pdf,.jpg,.jpeg,.png',
    icon: 'photo',
    defaultMode: 'hidden',
    scope: 'per_student',
    legacyIds: ['birth-certificate', 'birth_cert'],
  },
  {
    settingId: 'immunization_records',
    slotId: 'immunization-records',
    label: 'Immunization Records',
    description: 'Up-to-date immunization records.',
    accept: '.pdf,.jpg,.jpeg,.png',
    icon: 'file',
    defaultMode: 'hidden',
    scope: 'per_student',
    legacyIds: ['immunization-records', 'immunization'],
  },
  {
    settingId: 'occupational_history',
    slotId: 'occupational-history',
    label: 'School History',
    description: 'Current school, playgroup, or home-learning context.',
    accept: '.pdf,.jpg,.jpeg,.png',
    icon: 'file',
    defaultMode: 'hidden',
    scope: 'per_student',
    legacyIds: ['occupational-history', 'school_history'],
  },
  {
    settingId: 'profile_photo',
    slotId: 'recent-photo',
    label: 'Recent Photo',
    description: 'Recent student photo.',
    accept: '.jpg,.jpeg,.png',
    icon: 'photo',
    defaultMode: 'hidden',
    scope: 'per_student',
    legacyIds: ['recent-photo', 'photo'],
  },
  {
    settingId: 'iep_504_plan',
    slotId: 'iep-504-plan',
    label: 'IEP/504 Plan',
    description: 'Upload if applicable to this child.',
    accept: '.pdf,.jpg,.jpeg,.png',
    icon: 'file',
    defaultMode: 'hidden',
    scope: 'per_student',
    legacyIds: ['iep-504-plan', 'iep_504', 'iep', '504_plan'],
  },
];

export const DEFAULT_TUITION_POLICY_TEXT =
  'I/We understand that tuition and fee arrangements are completed during enrollment after an admissions decision.';

export const DEFAULT_CERTIFICATION_TEXT =
  'I certify that all information provided in this application is true and accurate to the best of my knowledge.';

const DEFAULT_VACCINATION_POLICY_TEXT =
  'The school requires students to have all state-required vaccinations and immunization records.';

const DEFAULT_VACCINATION_POLICY_NO_RELIGIOUS_EXEMPTIONS_TEXT =
  `${DEFAULT_VACCINATION_POLICY_TEXT} Religious exemptions will not be accepted.`;

const DEFAULT_VACCINATION_POLICY_RELIGIOUS_EXEMPTIONS_ALLOWED_TEXT =
  `${DEFAULT_VACCINATION_POLICY_TEXT} Religious exemption requests should be discussed with the school office.`;

const DEFAULT_VACCINATION_POLICY_UNKNOWN_EXEMPTIONS_TEXT =
  `${DEFAULT_VACCINATION_POLICY_TEXT} Please contact the school office for the current exemption policy.`;

const AGREEMENT_DEFINITIONS: AdmissionAgreementDefinition[] = [
  {
    id: 'tuition_policy',
    label: 'Tuition Policy Agreement',
    text: DEFAULT_TUITION_POLICY_TEXT,
    defaultMode: 'hidden',
  },
  {
    id: 'certification',
    label: 'Certification',
    text: DEFAULT_CERTIFICATION_TEXT,
    defaultMode: 'required',
  },
];

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const hasOwn = (record: Record<string, unknown> | undefined, key: string): boolean =>
  Boolean(record && Object.prototype.hasOwnProperty.call(record, key));

const readText = (value: unknown): string => String(value ?? '').trim();

const normalizeMode = (value: unknown): AdmissionRequirementMode | null => {
  const token = readText(value)
    .toLowerCase()
    .replace(/[^a-z]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (token === 'hidden') return 'hidden';
  if (token === 'optional') return 'optional';
  if (token === 'required') return 'required';
  return null;
};

const resolveLegacyMode = (
  row: Record<string, unknown> | undefined,
  defaultMode: AdmissionRequirementMode,
): AdmissionRequirementMode => {
  if (!row) return defaultMode;
  const explicitMode = normalizeMode(row.requirementMode ?? row.mode);
  if (explicitMode) return explicitMode;
  if (row.visible === false) return 'hidden';
  if (typeof row.required === 'boolean') {
    if (row.required === false && row.visible !== true) return 'hidden';
    return row.required ? 'required' : 'optional';
  }
  return defaultMode;
};

export const normalizeAdmissionGradeKey = (value: unknown): string => {
  const normalized = readText(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return '';
  const compact = normalized.replace(/\s+/g, '');
  if (normalized === 'n' || normalized.includes('nursery')) return 'nursery';
  if (normalized === 'k' || normalized === 'kg' || normalized.includes('kindergarten')) return 'kindergarten';
  if (compact === 'pre1a' || compact.includes('pre1a')) return 'pre_1a';
  const gradeMatch = normalized.match(/(?:^|\b)(?:grade|gr|g)?\s*(\d{1,2})(?:st|nd|rd|th)?(?:\b|$)/);
  if (gradeMatch?.[1]) return `grade_${Number(gradeMatch[1])}`;
  return normalized.replace(/\s+/g, '_');
};

const normalizeGradeScope = (
  values: unknown,
  allowedOptions: readonly string[] = ADMISSIONS_GRADE_SCOPE_OPTIONS,
): string[] => {
  if (!Array.isArray(values)) return [];
  const rows = values
    .map((entry) => readText(entry))
    .filter(Boolean);
  const byKey = new Map<string, string>();
  rows.forEach((entry) => {
    const key = normalizeAdmissionGradeKey(entry);
    if (!key) return;
    const canonical = allowedOptions.find(
      (option) => normalizeAdmissionGradeKey(option) === key,
    );
    if (!canonical) return;
    byKey.set(key, canonical);
  });
  return Array.from(byKey.values());
};

const normalizeDocumentRequirementId = (id: unknown): string =>
  readText(id)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const findDocumentSetting = (
  rows: Array<Record<string, unknown>>,
  definition: AdmissionDocumentDefinition,
): Record<string, unknown> | undefined => {
  const ids = new Set(
    [definition.settingId, definition.slotId, ...(definition.legacyIds ?? [])]
      .map(normalizeDocumentRequirementId),
  );
  return rows.find((row) =>
    ids.has(normalizeDocumentRequirementId(row.id)) ||
    ids.has(normalizeDocumentRequirementId(row.slotId)),
  );
};

const KNOWN_DOCUMENT_SETTING_IDS = new Set(
  DOCUMENT_DEFINITIONS.flatMap((definition) => [
    definition.settingId,
    definition.slotId,
    ...(definition.legacyIds ?? []),
  ]).map(normalizeDocumentRequirementId),
);

export const isKnownAdmissionsDocumentRequirementId = (id: unknown): boolean =>
  KNOWN_DOCUMENT_SETTING_IDS.has(normalizeDocumentRequirementId(id));

const getLegacyDocumentFallbackMode = (
  definition: AdmissionDocumentDefinition,
  hasPersistedDocumentRows: boolean,
): AdmissionRequirementMode => {
  if (!hasPersistedDocumentRows) return definition.defaultMode;
  return definition.settingId === 'occupational_history'
    ? 'required'
    : 'hidden';
};

export const getAdmissionsDocumentRequirementRows = (
  admissionConfig: unknown,
): AdmissionDocumentRequirementRow[] => {
  const config = asRecord(admissionConfig);
  const rawRows = Array.isArray(config.requiredDocuments)
    ? config.requiredDocuments.map(asRecord)
    : [];
  const hasPersistedDocumentRows = rawRows.length > 0;
  const transcriptGrades = normalizeGradeScope(
    config.transcriptGrades,
    ADMISSIONS_REPORT_CARD_GRADE_SCOPE_OPTIONS,
  );

  return DOCUMENT_DEFINITIONS.map((definition) => {
    const setting = findDocumentSetting(rawRows, definition);
    const hasExplicitVisibilityMode =
      Boolean(setting) &&
      (normalizeMode(setting?.requirementMode ?? setting?.mode) !== null ||
        hasOwn(setting, 'visible'));
    let mode = resolveLegacyMode(
      setting,
      getLegacyDocumentFallbackMode(
        definition,
        hasPersistedDocumentRows,
      ),
    );
    if (
      definition.settingId === 'transcripts'
      && typeof config.transcriptsRequired === 'boolean'
      && (!setting || !hasExplicitVisibilityMode)
    ) {
      mode = config.transcriptsRequired ? 'required' : 'hidden';
    }
    const hasPersistedGradeScope =
      Boolean(setting) &&
      (hasOwn(setting, 'gradeScope') || hasOwn(setting, 'grades'));
    const gradeScope = hasPersistedGradeScope
      ? normalizeGradeScope(
        setting?.gradeScope ?? setting?.grades,
        definition.defaultGradeScope ?? ADMISSIONS_GRADE_SCOPE_OPTIONS,
      )
      : [];
    const resolvedGradeScope = hasPersistedGradeScope
      ? gradeScope
      : definition.settingId === 'transcripts' && transcriptGrades.length > 0
        ? transcriptGrades
        : [...(definition.defaultGradeScope ?? [])];
    const label = readText(setting?.label) || definition.label;
    const description = readText(setting?.description) || definition.description;
    return {
      settingId: definition.settingId,
      slotId: definition.slotId,
      label,
      description,
      accept: definition.accept,
      icon: definition.icon,
      mode,
      visible: mode !== 'hidden',
      required: mode === 'required',
      gradeScope: resolvedGradeScope,
      scope: definition.scope,
    };
  });
};

export const getAdmissionsAgreementRows = (
  admissionConfig: unknown,
): AdmissionAgreementRow[] => {
  const config = asRecord(admissionConfig);
  const rawRows = Array.isArray(config.applicationAgreements)
    ? config.applicationAgreements.map(asRecord)
    : [];
  return AGREEMENT_DEFINITIONS.map((definition) => {
    const setting = rawRows.find((row) => readText(row.id) === definition.id);
    const mode = resolveLegacyMode(setting, definition.defaultMode);
    return {
      id: definition.id,
      label: readText(setting?.label) || definition.label,
      text: hasOwn(setting, 'text') ? String(setting?.text ?? '') : definition.text,
      mode,
      visible: mode !== 'hidden',
      required: mode === 'required',
    };
  });
};

export const serializeAdmissionsDocumentRequirementRows = (
  rows: AdmissionDocumentRequirementRow[],
) => rows.map((row) => ({
  id: row.settingId,
  label: row.label,
  required: row.required,
  visible: row.visible,
  requirementMode: row.mode,
  gradeScope: [...row.gradeScope],
  description: row.description,
  scope: row.scope,
}));

type SerializedAdmissionDocumentRequirement = ReturnType<
  typeof serializeAdmissionsDocumentRequirementRows
>[number];

const serializeUnknownAdmissionsDocumentRequirementRow = (
  value: unknown,
): SerializedAdmissionDocumentRequirement | null => {
  const row = asRecord(value);
  const id = readText(row.id);
  if (!id || isKnownAdmissionsDocumentRequirementId(id)) return null;
  const mode =
    normalizeMode(row.requirementMode ?? row.mode) ??
    (row.required === true
      ? 'required'
      : row.visible === false
        ? 'hidden'
        : 'optional');

  return {
    ...row,
    id,
    label: readText(row.label) || id,
    required: mode === 'required',
    visible: mode !== 'hidden',
    requirementMode: mode,
    gradeScope: normalizeGradeScope(row.gradeScope ?? row.grades),
    description: readText(row.description),
    scope: readText(row.scope) === 'family' ? 'family' : 'per_student',
  };
};

export const mergeAdmissionsDocumentRequirementRows = (
  existingRows: unknown,
  rows: AdmissionDocumentRequirementRow[],
): SerializedAdmissionDocumentRequirement[] => {
  const serializedRows = serializeAdmissionsDocumentRequirementRows(rows);
  const unknownRows = Array.isArray(existingRows)
    ? existingRows
        .map(serializeUnknownAdmissionsDocumentRequirementRow)
        .filter((row): row is SerializedAdmissionDocumentRequirement => row != null)
    : [];

  return [...unknownRows, ...serializedRows];
};

export const serializeAdmissionsAgreementRows = (
  rows: AdmissionAgreementRow[],
) => rows.map((row) => ({
  id: row.id,
  label: row.label,
  text: row.text,
  required: row.required,
  visible: row.visible,
  requirementMode: row.mode,
}));

export const isAdmissionRequirementInGradeScope = (
  row: Pick<AdmissionDocumentRequirementRow, 'gradeScope'>,
  grade: unknown,
): boolean => {
  if (row.gradeScope.length === 0) return true;
  const gradeKey = normalizeAdmissionGradeKey(grade);
  if (!gradeKey) return true;
  return row.gradeScope.some((scopeEntry) => normalizeAdmissionGradeKey(scopeEntry) === gradeKey);
};

export const isAdmissionsDocumentRowVisibleForStudent = (
  row: AdmissionDocumentRequirementRow,
  student: AdmissionsRequirementStudent | null,
): boolean => {
  if (!row.visible) return false;
  if (!student) return row.gradeScope.length === 0;
  return isAdmissionRequirementInGradeScope(row, student.grade);
};

export const resolveAdmissionsVaccinationPolicyText = (
  policy: AdmissionVaccinationPolicyLike | null | undefined,
): string => {
  const configuredText = readText(policy?.policyText);
  if (configuredText) return configuredText;
  if (policy?.allowReligiousExemptions === true) {
    return DEFAULT_VACCINATION_POLICY_RELIGIOUS_EXEMPTIONS_ALLOWED_TEXT;
  }
  if (policy?.allowReligiousExemptions === false) {
    return DEFAULT_VACCINATION_POLICY_NO_RELIGIOUS_EXEMPTIONS_TEXT;
  }
  return DEFAULT_VACCINATION_POLICY_UNKNOWN_EXEMPTIONS_TEXT;
};

export const isBothParentVaccinationAcknowledgementRequired = (
  policy: AdmissionVaccinationPolicyLike | null | undefined,
): boolean => policy?.requireBothParentsAcknowledgment === true;

export const resolveAdmissionsDocumentUploadFileName = (input: {
  documentUploads: Record<string, string | undefined>;
  slotId: string;
  studentId?: string | null;
  studentIndex: number;
  studentCount?: number;
}): string => {
  const studentId = readText(input.studentId);
  const bareKeysAllowed =
    (input.studentCount ?? 1) <= 1 || input.studentIndex === 0;
  const primaryKey = studentId
    ? buildDocumentSlotKey(input.slotId, studentId)
    : bareKeysAllowed
      ? input.slotId
      : '';
  const positionalKey = buildDocumentSlotKey(input.slotId, `student_${input.studentIndex}`);
  const bareKeyValue = bareKeysAllowed
    ? input.documentUploads[input.slotId]
    : undefined;
  return (
    (primaryKey ? input.documentUploads[primaryKey] : undefined) ??
    input.documentUploads[positionalKey] ??
    bareKeyValue ??
    ''
  );
};

const buildDocumentSlotKey = (slotId: string, studentId?: string | null): string =>
  studentId ? `${studentId}::${slotId}` : slotId;

const getStudentLabel = (
  student: AdmissionsRequirementStudent | null,
  index: number,
): string => {
  if (!student) return '';
  const name = `${readText(student.firstName)} ${readText(student.lastName)}`.trim();
  return name || `Student ${index + 1}`;
};

export const buildAdmissionsIntakeRequiredUploads = (input: {
  rows: AdmissionDocumentRequirementRow[];
  students: AdmissionsRequirementStudent[];
  documentUploads: Record<string, string | undefined>;
}): AdmissionsRequiredUpload[] => {
  const includeStudentLabel = input.students.length > 1;
  const targets = input.students.length > 0
    ? input.students.map((student, index) => ({ student, index }))
    : [{ student: null, index: 0 }];

  return targets.flatMap(({ student, index }) =>
    input.rows
      .filter((row) => row.required)
      .filter((row) => isAdmissionsDocumentRowVisibleForStudent(row, student))
      .map((row) => {
        const uploadStudentId = readText(student?.id) || (input.students.length > 1 ? `student_${index}` : null);
        const key = buildDocumentSlotKey(row.slotId, uploadStudentId);
        const studentLabel = includeStudentLabel ? getStudentLabel(student, index) : '';
        return {
          key,
          label: studentLabel ? `${studentLabel}: ${row.label}` : row.label,
          fileName: resolveAdmissionsDocumentUploadFileName({
            documentUploads: input.documentUploads,
            slotId: row.slotId,
            studentId: uploadStudentId,
            studentIndex: index,
            studentCount: input.students.length,
          }),
        };
      }),
  );
};
